import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Typography, Button, Toolbar, useTheme, Chip } from '@mui/material';
import { keyframes } from '@emotion/react';

// ─── Keyframes ────────────────────────────────────────────────────────────────

const floatLeft = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-3deg); }
  50%       { transform: translateY(-18px) rotate(-3deg); }
`;

const floatRight = keyframes`
  0%, 100% { transform: translateY(0px) rotate(3deg); }
  50%       { transform: translateY(-12px) rotate(3deg); }
`;

const floatStraight = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-14px); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(76,175,80,0.4); }
  50%       { opacity: 0.85; box-shadow: 0 0 0 6px rgba(76,175,80,0); }
`;

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// ─── Football / soccer term based on timezone ─────────────────────────────────

function useFootballTerm() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  // US and Australia call it "soccer"; rest of world (including UK/Europe) calls it "football"
  if (tz.startsWith('America/') || tz.startsWith('Australia/')) return 'soccer';
  return 'football';
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useScrollProgress() {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    function update() {
      const scrollH = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(scrollH > 0 ? window.scrollY / scrollH : 0);
    }
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, []);
  return progress;
}

function useParallax(speed = 0.2) {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    function update() { setOffset(window.scrollY * speed); }
    window.addEventListener('scroll', update, { passive: true });
    return () => window.removeEventListener('scroll', update);
  }, [speed]);
  return offset;
}

function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function useCounter(target, inView, duration = 1400) {
  const [count, setCount] = useState(0);
  const rafRef  = useRef(null);
  const startRef = useRef(null);
  const animate = useCallback((now) => {
    if (!startRef.current) startRef.current = now;
    const elapsed  = now - startRef.current;
    const progress = Math.min(elapsed / duration, 1);
    const eased    = 1 - Math.pow(1 - progress, 3);
    setCount(Math.round(eased * target));
    if (progress < 1) rafRef.current = requestAnimationFrame(animate);
  }, [target, duration]);
  useEffect(() => {
    if (!inView) return;
    startRef.current = null;
    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [inView, animate]);
  return count;
}

function AnimateIn({ children, delay = 0, direction = 'up', sx = {} }) {
  const [ref, inView] = useInView();
  const offsets = { up: '0, 40px', down: '0, -40px', left: '-24px, 0', right: '24px, 0' };
  const [x, y] = (offsets[direction] ?? offsets.up).split(',');
  return (
    <Box
      ref={ref}
      sx={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translate(0,0)' : `translate(${x},${y})`,
        transition: `opacity 0.7s ease ${delay}s, transform 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// ─── Features data ─────────────────────────────────────────────────────────────

const FEATURES = [
  { icon: '⚡', title: 'Real-Time Updates',     description: 'Goals, cards, substitutions, and lineups delivered the moment they happen.',      color: '#f59e0b' },
  { icon: '🌍', title: '1000+ Leagues',          description: 'From the Premier League to the J-League — every competition, everywhere.',         color: '#3b82f6' },
  { icon: '🙈', title: 'Spoiler Mode',           description: 'Hide scores and events for any team or league. Watch the game later, your way.',   color: '#8b5cf6' },
  { icon: '⌚', title: 'Apple Watch',            description: 'Live scores on your wrist. Glance and go — no phone needed.',                      color: '#ec4899' },
  { icon: '🏟️', title: '3D Stadium Maps',        description: 'Explore matchday venues in immersive 3D detail.',                                  color: '#10b981' },
  { icon: '🔔', title: 'Smart Notifications',    description: 'Push alerts with built-in spoiler protection, so you always stay in the loop.',   color: '#f97316' },
];

// ─── Sub-components ────────────────────────────────────────────────────────────

function ScrollProgressBar() {
  const progress = useScrollProgress();
  return (
    <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, height: 2, zIndex: 1101, pointerEvents: 'none' }}>
      <Box sx={{
        height: '100%',
        background: 'linear-gradient(90deg, #22c55e, #3b82f6)',
        width: `${progress * 100}%`,
        borderRadius: '0 2px 2px 0',
        transition: 'width 0.08s linear',
      }} />
    </Box>
  );
}

function DrawUnderline({ inView, delay = 0.3 }) {
  return (
    <Box sx={{
      height: 3,
      borderRadius: 2,
      background: 'linear-gradient(90deg, #22c55e, #3b82f6)',
      width: inView ? 48 : 0,
      transition: `width 0.7s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
      mt: 1,
      mb: 2,
    }} />
  );
}

function AppStoreButton({ size = 'large' }) {
  return (
    <Button
      variant="contained"
      size={size}
      href="https://apps.apple.com/us/app/turf-soccer-scores-news/id6748669110"
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        borderRadius: 3,
        px: size === 'large' ? 4 : 3,
        py: size === 'large' ? 1.5 : 1,
        fontWeight: 700,
        fontSize: size === 'large' ? '1rem' : '0.875rem',
        textTransform: 'none',
        background: 'linear-gradient(135deg, #22c55e, #16a34a)',
        boxShadow: '0 4px 24px rgba(34,197,94,0.35)',
        '&:hover': {
          background: 'linear-gradient(135deg, #16a34a, #15803d)',
          boxShadow: '0 6px 32px rgba(34,197,94,0.45)',
          transform: 'translateY(-2px)',
        },
        transition: 'all 0.2s ease',
      }}
    >
      Download on the App Store — Free
    </Button>
  );
}

// Phone image — images already include the device frame
function PhoneImage({ src, alt, tilt = 0, animDuration = '4.5s', animDelay = '0s', parallaxOffset = 0, width, height }) {
  const sizeSx = height
    ? { width: 'auto', height: { xs: Math.round(height * 0.7), sm: height } }
    : { width: { xs: Math.round((width ?? 200) * 0.78), sm: width ?? 200 }, height: 'auto' };

  return (
    // Parallax wrapper
    <Box sx={{ transform: `translateY(${-parallaxOffset}px)`, willChange: 'transform' }}>
      {/* Static rotation — always applied, never overridden by animation */}
      <Box sx={{ transform: `rotate(${tilt}deg)` }}>
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{
            ...sizeSx,
            display: 'block',
            filter: 'drop-shadow(0 32px 64px rgba(0,0,0,0.45))',
            animation: `${floatStraight} ${animDuration} ease-in-out infinite`,
            animationDelay: animDelay,
          }}
        />
      </Box>
    </Box>
  );
}

function StatCounter({ value, label, numericTarget, suffix = '' }) {
  const [ref, inView] = useInView(0.5);
  const count = useCounter(numericTarget ?? 0, inView);
  const display = numericTarget != null ? `${count}${suffix}` : value;
  return (
    <Box ref={ref}>
      <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', lineHeight: 1 }}>{display}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
    </Box>
  );
}

function SectionHeading({ overline, title, body, align = 'left' }) {
  const [ref, inView] = useInView(0.1);
  return (
    <Box ref={ref} sx={{ textAlign: align }}>
      <Box sx={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(20px)',
        transition: 'opacity 0.6s ease, transform 0.6s cubic-bezier(0.22,1,0.36,1)',
      }}>
        <Typography variant="overline" sx={{ color: '#22c55e', fontWeight: 700, letterSpacing: 2 }}>
          {overline}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
        <DrawUnderline inView={inView} delay={0.15} />
      </Box>

      <Box sx={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        transition: 'opacity 0.65s ease 0.1s, transform 0.65s cubic-bezier(0.22,1,0.36,1) 0.1s',
      }}>
        <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: -1, mb: 2, fontSize: { xs: '2rem', md: '2.8rem' } }}>
          {title}
        </Typography>
      </Box>

      {body && (
        <Box sx={{
          opacity: inView ? 1 : 0,
          transform: inView ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.65s ease 0.18s, transform 0.65s cubic-bezier(0.22,1,0.36,1) 0.18s',
        }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 500, mb: 6, lineHeight: 1.75, mx: align === 'center' ? 'auto' : 0 }}>
            {body}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

function FeatureCard({ icon, title, description, color, delay }) {
  const [ref, inView] = useInView(0.08);
  return (
    <Box
      ref={ref}
      sx={{
        flex: '1 1 280px',
        maxWidth: { xs: '100%', sm: 360 },
        p: 3,
        borderRadius: '20px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0) scale(1)' : 'translateY(32px) scale(0.96)',
        transition: `opacity 0.6s ease ${delay}s, transform 0.6s cubic-bezier(0.22,1,0.36,1) ${delay}s`,
        '&:hover': {
          borderColor: color,
          transform: 'translateY(-6px) scale(1)',
          boxShadow: `0 16px 48px ${color}28, 0 0 0 1px ${color}`,
          transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
        },
      }}
    >
      <Box sx={{
        fontSize: 34, mb: 1.5, lineHeight: 1, display: 'inline-block',
        opacity: inView ? 1 : 0,
        transform: inView ? 'scale(1)' : 'scale(0.3)',
        transition: `opacity 0.4s ease ${delay + 0.15}s, transform 0.5s cubic-bezier(0.34,1.56,0.64,1) ${delay + 0.15}s`,
      }}>
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.75 }}>{title}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.65 }}>{description}</Typography>
    </Box>
  );
}

// ─── Device SVG icons ──────────────────────────────────────────────────────────

function IconIPhone() {
  return (
    <svg width="28" height="52" viewBox="0 0 28 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="26" height="50" rx="6" stroke="currentColor" strokeWidth="1.8"/>
      {/* Dynamic island */}
      <rect x="9" y="4.5" width="10" height="3" rx="1.5" fill="currentColor"/>
      {/* Home bar */}
      <rect x="9" y="44.5" width="10" height="2" rx="1" fill="currentColor"/>
    </svg>
  );
}

function IconIPad() {
  return (
    <svg width="40" height="52" viewBox="0 0 40 52" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="38" height="50" rx="6" stroke="currentColor" strokeWidth="1.8"/>
      {/* Camera */}
      <circle cx="20" cy="4.5" r="1.8" fill="currentColor"/>
      {/* Home bar */}
      <rect x="14" y="44.5" width="12" height="2" rx="1" fill="currentColor"/>
    </svg>
  );
}

function IconMac() {
  return (
    <svg width="58" height="44" viewBox="0 0 58 44" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Screen */}
      <rect x="4" y="1" width="50" height="32" rx="4" stroke="currentColor" strokeWidth="1.8"/>
      {/* Camera dot */}
      <circle cx="29" cy="5" r="1.3" fill="currentColor"/>
      {/* Base — slightly wider than screen */}
      <path d="M1 37 L4 33 L54 33 L57 37 L57 41 Q57 43 55 43 L3 43 Q1 43 1 41 Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" fill="none"/>
      {/* Notch */}
      <path d="M21 43 Q29 41 37 43" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
    </svg>
  );
}

function IconWatch() {
  // All elements centred at x=16. Body: x=2 w=28 → centre 16. Bands: x=8 w=16 → centre 16.
  return (
    <svg width="36" height="50" viewBox="0 0 36 50" fill="none" xmlns="http://www.w3.org/2000/svg">
      {/* Top band */}
      <rect x="8" y="1" width="16" height="9" rx="2.5" stroke="currentColor" strokeWidth="1.8"/>
      {/* Body */}
      <rect x="2" y="10" width="28" height="30" rx="8" stroke="currentColor" strokeWidth="1.8"/>
      {/* Digital Crown — flush against right edge of body */}
      <rect x="30" y="16" width="4" height="8" rx="2" stroke="currentColor" strokeWidth="1.5"/>
      {/* Bottom band */}
      <rect x="8" y="40" width="16" height="9" rx="2.5" stroke="currentColor" strokeWidth="1.8"/>
    </svg>
  );
}

function PlatformCard({ label, Icon, sub, delay }) {
  const [ref, inView] = useInView(0.1);
  return (
    <Box
      ref={ref}
      sx={{
        px: 4, py: 3.5, borderRadius: '20px',
        border: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper',
        minWidth: 140, textAlign: 'center',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.92)',
        transition: `opacity 0.55s ease ${delay}s, transform 0.55s cubic-bezier(0.34,1.2,0.64,1) ${delay}s`,
        '&:hover': {
          borderColor: '#22c55e',
          transform: 'translateY(-6px) scale(1)',
          boxShadow: '0 16px 48px rgba(34,197,94,0.14), 0 0 0 1px #22c55e',
          transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
        },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 52, mb: 1.5, color: theme => theme.palette.mode === 'dark' ? '#ffffff' : '#333333' }}>
        <Icon />
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{label}</Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{sub}</Typography>
    </Box>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export default function Landing() {
  const theme        = useTheme();
  const isDark       = theme.palette.mode === 'dark';
  const sportTerm    = useFootballTerm(); // 'football' or 'soccer'
  const SportTerm    = sportTerm.charAt(0).toUpperCase() + sportTerm.slice(1);

  const phone1Parallax = useParallax(0.12);
  const phone2Parallax = useParallax(0.2);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', overflowX: 'hidden' }}>
      <Helmet>
        <title>Turf — Football Scores, Fixtures &amp; News</title>
        <meta name="description" content="Live scores, fixtures, results, and news across 1000+ leagues. Available on iPhone, iPad, Apple Watch, and Mac." />
        <link rel="canonical" href="https://turf-app.io/" />
        <meta property="og:url" content="https://turf-app.io/" />
        <meta property="og:title" content="Turf — Football Scores, Fixtures &amp; News" />
        <meta property="og:description" content="Live scores, fixtures, results, and news across 1000+ leagues. Available on iPhone, iPad, Apple Watch, and Mac." />
        <meta name="twitter:url" content="https://turf-app.io/" />
        <meta name="twitter:title" content="Turf — Football Scores, Fixtures &amp; News" />
        <meta name="twitter:description" content="Live scores, fixtures, results, and news across 1000+ leagues. Available on iPhone, iPad, Apple Watch, and Mac." />
      </Helmet>
      <Toolbar />

      {/* ── Hero ── */}
      <Box
        sx={{
          minHeight: '92vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
          px: { xs: 3, md: 8 },
          py: { xs: 6, md: 4 },
        }}
      >
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: isDark
            ? 'radial-gradient(ellipse 70% 60% at 70% 50%, rgba(34,197,94,0.08) 0%, transparent 70%), radial-gradient(ellipse 50% 40% at 30% 20%, rgba(59,130,246,0.06) 0%, transparent 60%)'
            : 'radial-gradient(ellipse 70% 60% at 70% 50%, rgba(34,197,94,0.06) 0%, transparent 70%)',
          animation: `${fadeIn} 1.2s ease`,
        }} />

        <Box sx={{ maxWidth: 1280, mx: 'auto', width: '100%', display: 'flex', alignItems: 'center', gap: { xs: 4, lg: 8 }, flexWrap: 'wrap' }}>

          {/* Left — copy */}
          <Box sx={{ flex: '1 1 340px', maxWidth: 560, animation: `${fadeIn} 0.8s ease` }}>
            <Chip
              label="Now available on the App Store"
              size="small"
              sx={{
                mb: 3,
                bgcolor: isDark ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.1)',
                color: '#22c55e',
                border: '1px solid rgba(34,197,94,0.3)',
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
            <Typography
              variant="h1"
              sx={{ fontSize: { xs: '2.8rem', md: '3.8rem', lg: '4.4rem' }, fontWeight: 900, lineHeight: 1.05, letterSpacing: -2, mb: 3 }}
            >
              The ultimate{' '}
              <Box
                component="span"
                sx={{
                  background: 'linear-gradient(90deg, #22c55e, #3b82f6, #22c55e)',
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  animation: `${shimmer} 4s linear infinite`,
                  display: 'inline',
                }}
              >
                {sportTerm}
              </Box>
              {' '}companion.
            </Typography>
            <Typography
              variant="h6"
              sx={{ color: 'text.secondary', fontWeight: 400, lineHeight: 1.7, mb: 4, fontSize: { xs: '1rem', md: '1.15rem' } }}
            >
              {SportTerm} scores, stats, and live updates from over 1000 leagues and competitions worldwide.
              Beautifully simple. Wonderfully powerful.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 4 }}>
              <AppStoreButton size="large" />
            </Box>
          </Box>

          {/* Right — real phone screenshots with parallax */}
          <Box
            sx={{
              flex: '1 1 300px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-end',
              gap: { xs: 2, md: 3 },
              animation: `${fadeIn} 1s ease 0.2s both`,
              minHeight: { xs: 360, md: 480 },
            }}
          >
            <Box sx={{ mb: { xs: 4, md: 8 } }}>
              <PhoneImage
                src="/resources/product-images/hero/iphone-fixture-list.png"
                alt="Turf scores list"
                tilt={-3}
                animDuration="4.5s"
                animDelay="0s"
                parallaxOffset={phone1Parallax}
                width={250}
              />
            </Box>
            <PhoneImage
              src="/resources/product-images/hero/iphone-fixture.png"
              alt="Turf fixture detail"
              tilt={3}
              animDuration="5.5s"
              animDelay="0.8s"
              parallaxOffset={phone2Parallax}
              width={250}
            />
          </Box>
        </Box>
      </Box>

      {/* ── Features grid ── */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 8, md: 12 } }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
          <SectionHeading
            overline="Everything you need"
            title="Follow the beautiful game, your way."
            body={`Built by a ${sportTerm} fan who was tired of seeing scores before watching the game. Every feature exists because it had to.`}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {FEATURES.map((f, i) => (
              <FeatureCard key={f.title} {...f} delay={i * 0.07} />
            ))}
          </Box>
        </Box>
      </Box>

      {/* ── News section ── */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 8, md: 12 }, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', alignItems: 'center', gap: { xs: 6, md: 12 }, flexWrap: 'wrap-reverse' }}>

          {/* Phone image */}
          <AnimateIn direction="left" sx={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center', width: { xs: '100%', md: 'auto' } }}>
            <PhoneImage
              src="/resources/product-images/hero/iphone-news.png"
              alt="Turf news feed"
              tilt={-2}
              animDuration="5s"
              width={220}
            />
          </AnimateIn>

          {/* Copy */}
          <Box sx={{ flex: '1 1 300px', maxWidth: 480 }}>
            <AnimateIn direction="right">
              <Typography variant="overline" sx={{ color: '#22c55e', fontWeight: 700, letterSpacing: 2 }}>
                Stay informed
              </Typography>
              <DrawUnderline inView delay={0.1} />
            </AnimateIn>
            <AnimateIn delay={0.1} direction="right">
              <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: -1, mb: 2, fontSize: { xs: '2rem', md: '2.8rem' } }}>
                {SportTerm} news, your way.
              </Typography>
            </AnimateIn>
            <AnimateIn delay={0.18} direction="right">
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 4 }}>
                The News tab brings you the latest {sportTerm} coverage from sources you trust —
                with spoiler-safe browsing built in. Catch up on the day's stories without
                worrying about seeing a result before you're ready.
              </Typography>
            </AnimateIn>
            <AnimateIn delay={0.26} direction="right">
              <AppStoreButton size="medium" />
            </AnimateIn>
          </Box>
        </Box>
      </Box>

      {/* ── Spoiler Mode spotlight ── */}
      <Box
        sx={{
          mx: { xs: 2, md: 6 },
          my: { xs: 8, md: 12 },
          borderRadius: 6,
          overflow: 'hidden',
          position: 'relative',
          background: isDark
            ? 'linear-gradient(135deg, #1e0a3c 0%, #0f1923 50%, #1a0a2e 100%)'
            : 'linear-gradient(135deg, #f3e8ff 0%, #ede9fe 100%)',
          border: '1px solid',
          borderColor: isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.2)',
        }}
      >
        <Box sx={{
          position: 'absolute', top: '-20%', right: '-10%',
          width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.15) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />

        <Box sx={{
          display: 'flex', alignItems: 'center', gap: { xs: 4, md: 8 }, flexWrap: 'wrap',
          px: { xs: 4, md: 8 }, py: { xs: 6, md: 8 }, position: 'relative',
        }}>
          <Box sx={{ flex: '1 1 280px', maxWidth: 480 }}>
            <AnimateIn direction="left">
              <Box sx={{ display: 'inline-flex', alignItems: 'center', px: 1.5, py: 0.5, bgcolor: 'rgba(139,92,246,0.15)', borderRadius: 999, border: '1px solid rgba(139,92,246,0.3)', mb: 2.5 }}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#a78bfa', letterSpacing: 0.5 }}>SPOILER MODE</Typography>
              </Box>
            </AnimateIn>
            <AnimateIn delay={0.1} direction="left">
              <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: -1, mb: 2, fontSize: { xs: '1.9rem', md: '2.6rem' }, color: isDark ? '#fff' : 'text.primary' }}>
                Watch on your own terms.
              </Typography>
            </AnimateIn>
            <AnimateIn delay={0.18} direction="left">
              <Typography variant="body1" sx={{ color: isDark ? 'rgba(255,255,255,0.6)' : 'text.secondary', lineHeight: 1.75, mb: 3 }}>
                Recorded the match? Running late? With Spoiler Mode, scores and events are hidden
                for the teams and leagues you choose. Browse Turf safely — you'll only see what
                you want to see.
              </Typography>
            </AnimateIn>
            <AnimateIn delay={0.26} direction="left">
              <AppStoreButton size="medium" />
            </AnimateIn>
          </Box>

          <AnimateIn delay={0.08} direction="right" sx={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: { xs: 2, md: 3 }, width: { xs: '100%', md: 'auto' } }}>
            <Box sx={{ mb: { xs: 4, md: 8 }, filter: 'drop-shadow(0 32px 64px rgba(139,92,246,0.25))' }}>
              <PhoneImage
                src="/resources/product-images/spoiler-mode/iphone-spoiler-mode-news.png"
                alt="Spoiler mode on iPhone — news"
                tilt={-3}
                animDuration="5s"
                animDelay="0s"
                height={390}
              />
            </Box>
            <Box sx={{ filter: 'drop-shadow(0 32px 64px rgba(139,92,246,0.35))' }}>
              <PhoneImage
                src="/resources/product-images/spoiler-mode/ipad-spoiler-mode-fixture.png"
                alt="Spoiler mode on iPad"
                tilt={0}
                animDuration="6.5s"
                animDelay="0.4s"
                height={480}
              />
            </Box>
            <Box sx={{ mb: { xs: 4, md: 8 }, filter: 'drop-shadow(0 32px 64px rgba(139,92,246,0.25))' }}>
              <PhoneImage
                src="/resources/product-images/spoiler-mode/iphone-spoiler-mode-fixture.png"
                alt="Spoiler mode on iPhone — fixture"
                tilt={3}
                animDuration="5.5s"
                animDelay="0.8s"
                height={390}
              />
            </Box>
          </AnimateIn>
        </Box>
      </Box>

      {/* ── Apple Watch section ── */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 8, md: 12 }, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto', display: 'flex', alignItems: 'center', gap: { xs: 6, md: 12 }, flexWrap: 'wrap' }}>

          {/* Copy */}
          <Box sx={{ flex: '1 1 300px', maxWidth: 480 }}>
            <AnimateIn direction="left">
              <Typography variant="overline" sx={{ color: '#ec4899', fontWeight: 700, letterSpacing: 2 }}>
                Apple Watch
              </Typography>
              <Box sx={{
                height: 3, borderRadius: 2,
                background: 'linear-gradient(90deg, #ec4899, #8b5cf6)',
                width: 48, mt: 1, mb: 2,
              }} />
            </AnimateIn>
            <AnimateIn delay={0.1} direction="left">
              <Typography variant="h3" sx={{ fontWeight: 800, letterSpacing: -1, mb: 2, fontSize: { xs: '2rem', md: '2.8rem' } }}>
                Scores on your wrist.
              </Typography>
            </AnimateIn>
            <AnimateIn delay={0.18} direction="left">
              <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.75, mb: 4 }}>
                Follow your favourite teams without reaching for your phone. Live scores,
                match events, and results stream straight to your Apple Watch —
                so you never miss a moment, wherever you are.
              </Typography>
            </AnimateIn>
            <AnimateIn delay={0.26} direction="left">
              <AppStoreButton size="medium" />
            </AnimateIn>
          </Box>

          {/* Two watch images */}
          <AnimateIn delay={0.08} direction="right" sx={{ flex: '0 0 auto', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: { xs: 2, md: 3 }, width: { xs: '100%', md: 'auto' } }}>
            <Box sx={{ mb: { xs: 3, md: 6 } }}>
              <PhoneImage
                src="/resources/product-images/hero/watch-fixture-list.png"
                alt="Turf on Apple Watch — scores list"
                tilt={-4}
                animDuration="4.5s"
                animDelay="0s"
                width={180}
              />
            </Box>
            <PhoneImage
              src="/resources/product-images/hero/watch-fixture.png"
              alt="Turf on Apple Watch — fixture detail"
              tilt={4}
              animDuration="5.5s"
              animDelay="0.6s"
              width={180}
            />
          </AnimateIn>
        </Box>
      </Box>

      {/* ── Platforms — with iPad showcase ── */}
      <Box sx={{ px: { xs: 3, md: 8 }, py: { xs: 8, md: 12 }, borderTop: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
          <SectionHeading
            align="center"
            overline="Available on"
            title="Across all your Apple devices."
            body="iPhone, iPad, Mac (M1+), and Apple Watch — one app, every screen."
          />

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: { xs: 6, md: 10 } }}>
            {[
              { label: 'iPhone',      Icon: IconIPhone, sub: 'iOS 18.0+' },
              { label: 'iPad',        Icon: IconIPad,   sub: 'iPadOS 18.0+' },
              { label: 'Mac',         Icon: IconMac,    sub: 'macOS 15.0+ (M1)' },
              { label: 'Apple Watch', Icon: IconWatch,  sub: 'watchOS 26+' },
            ].map(({ label, Icon, sub }, i) => (
              <PlatformCard key={label} label={label} Icon={Icon} sub={sub} delay={i * 0.1} />
            ))}
          </Box>

          {/* iPad showcase image with watch overlay */}
          <AnimateIn>
            <Box
              sx={{
                position: 'relative',
                borderRadius: 4,
                overflow: 'visible',
                maxWidth: 900,
                mx: 'auto',
              }}
            >
              {/* iPad */}
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  boxShadow: isDark
                    ? '0 40px 80px rgba(0,0,0,0.5)'
                    : '0 40px 80px rgba(0,0,0,0.12)',
                }}
              >
                <Box
                  component="img"
                  src="/resources/product-images/hero/ipad-fixture.png"
                  alt="Turf on iPad"
                  sx={{ width: '100%', height: 'auto', display: 'block' }}
                />
              </Box>

              {/* iPhone overlay — bottom-left corner */}
              <Box
                component="img"
                src="/resources/product-images/hero/iphone-fixture.png"
                alt="Turf on iPhone"
                sx={{
                  position: 'absolute',
                  bottom: { xs: -28, md: -40 },
                  left: { xs: -12, md: -28 },
                  width: { xs: 150, sm: 210, md: 270 },
                  height: 'auto',
                  filter: 'drop-shadow(0 16px 32px rgba(0,0,0,0.45))',
                  animation: `${keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}`} 7s ease-in-out infinite`,
                }}
              />

              {/* Watch overlay — bottom-right corner */}
              <Box
                component="img"
                src="/resources/product-images/hero/watch-fixture.png"
                alt="Turf on Apple Watch"
                sx={{
                  position: 'absolute',
                  bottom: { xs: -28, md: -40 },
                  right: { xs: -12, md: -28 },
                  width: { xs: 90, sm: 128, md: 160 },
                  height: 'auto',
                  filter: 'drop-shadow(0 16px 32px rgba(0,0,0,0.45))',
                  animation: `${keyframes`0%,100%{transform:translateY(0)}50%{transform:translateY(-6px)}`} 9s ease-in-out infinite 1s`,
                }}
              />
            </Box>
          </AnimateIn>
        </Box>
      </Box>

      {/* ── Final CTA ── */}
      <Box
        sx={{
          px: { xs: 3, md: 8 }, py: { xs: 10, md: 14 },
          textAlign: 'center', position: 'relative', overflow: 'hidden',
          borderTop: '1px solid', borderColor: 'divider',
        }}
      >
        <Box sx={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          background: isDark
            ? 'radial-gradient(ellipse 60% 60% at 50% 100%, rgba(34,197,94,0.07) 0%, transparent 70%)'
            : 'radial-gradient(ellipse 60% 60% at 50% 100%, rgba(34,197,94,0.05) 0%, transparent 70%)',
        }} />
        <AnimateIn>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900, letterSpacing: -1.5, mb: 2,
              fontSize: { xs: '2.2rem', md: '3.5rem' },
              background: 'linear-gradient(90deg, #22c55e, #3b82f6, #22c55e)',
              backgroundSize: '200% auto',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: `${shimmer} 5s linear infinite`,
            }}
          >
            Ready to kick off?
          </Typography>
        </AnimateIn>
        <AnimateIn delay={0.12}>
          <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 400, mb: 5, maxWidth: 440, mx: 'auto', lineHeight: 1.65 }}>
            Download Turf for free and never miss a moment of the beautiful game.
          </Typography>
        </AnimateIn>
        <AnimateIn delay={0.22}>
          <AppStoreButton size="large" />
        </AnimateIn>
      </Box>

    </Box>
  );
}
